import {
  IVesselParticularsLabels,
  composeVesselParticularsSheets,
} from "@tedivo/tedivo-bay-grid-core";

import { buildVesselParticularsData } from "@tedivo/tedivo-bay-grid-pure";
import { getTranslation } from "../../../../app/i18/i18tn";
import globalUnits from "../../../../app/units/globalUnits";
import ovdJsonStore from "../../../../app/stores/OVDJsonStore";

/**
 * Menu action: builds the container vessel particulars (side view, per-bay 20'/40'
 * count tables and one cross-section per group of identical bays) and opens it
 * in a new window ready to be printed / saved as PDF (A3 landscape).
 */
export function createVesselParticularsPrintAction() {
  return async () => {
    const json = ovdJsonStore.currentJson;
    if (!json) return;

    const planData = buildVesselParticularsData({
      shipData: json.shipData,
      sizeSummary: json.sizeSummary,
      baysData: json.baysData,
      lidData: json.lidData,
    });

    // Reuse the side view already rendered in the page
    const sideViewInDom = document.getElementById("sideViewSvg") as SVGSVGElement | null;
    const sideViewSvg = sideViewInDom
      ? (sideViewInDom.cloneNode(true) as SVGSVGElement)
      : undefined;
    sideViewSvg?.removeAttribute("id");

    const labels: IVesselParticularsLabels = {
      title: getTranslation("view:exportViews.vesselParticulars.title"),
      onDeck: getTranslation("view:exportViews.vesselParticulars.onDeck"),
      inHold: getTranslation("view:exportViews.vesselParticulars.inHold"),
      bay: getTranslation("view:exportViews.vesselParticulars.bay"),
      lcg: getTranslation("view:lcg"),
      teu: getTranslation("view:exportViews.vesselParticulars.teu"),
      loa: getTranslation("view:exportViews.vesselParticulars.loa"),
      lpp: getTranslation("view:exportViews.vesselParticulars.lpp"),
      imo: getTranslation("view:imoCode"),
      callSign: getTranslation("view:callSign"),
      total: getTranslation("view:exportViews.vesselParticulars.total"),
      generatedBy: getTranslation("view:exportViews.vesselParticulars.generatedBy"),
    };

    const sheets = composeVesselParticularsSheets({
      planData,
      sideViewSvg,
      labels,
      units: {
        lengthUnits: globalUnits.lengthUnits,
        tcgTransposer: globalUnits.tcgTransposer,
        vcgTransposer: globalUnits.vcgTransposer,
        lcgTransposer: globalUnits.lcgTransposer,
      },
    });

    openPrintWindow(sheets, json.shipData.shipName);
  };
}

function openPrintWindow(sheets: SVGSVGElement[], shipName: string | undefined) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const doc = printWindow.document;

  doc.open();
  doc.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(
      `${shipName || "-"} - ${getTranslation("view:exportViews.vesselParticulars.title")}`,
    )}</title><style>
      @page { size: A3 landscape; margin: 0; }
      html, body { margin: 0; padding: 0; background: #eee; }
      svg.vessel-particulars-sheet { display: block; width: 420mm; height: 297mm; margin: 0 auto; background: #fff; }
      @media print { html, body { background: #fff; } svg.vessel-particulars-sheet { page-break-after: always; margin: 0; } }
    </style></head><body></body></html>`,
  );
  doc.close();

  sheets.forEach((sheet) => {
    doc.body.appendChild(doc.importNode(sheet, true));
  });

  // Give the new window a tick to layout before opening the print dialog
  printWindow.setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 250);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
